/* eslint-disable react-refresh/only-export-components --
 * Context module: Provider + hook are intentionally co-located.
 */
import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/** `source key -> qid -> answer`, resolved by the backend for this visit. */
export type ExternalAnswerMap = Readonly<
  Record<string, Readonly<Record<string, string>>>
>;

export type ExternalAnswerState = {
  answers: ExternalAnswerMap;
};

const ExternalAnswerContext = createContext<ExternalAnswerState>({
  answers: {},
});

export function ExternalAnswerProvider({
  value,
  children,
}: {
  value: ExternalAnswerState;
  children: ReactNode;
}) {
  return (
    <ExternalAnswerContext.Provider value={value}>
      {children}
    </ExternalAnswerContext.Provider>
  );
}

export function useExternalAnswers(): ExternalAnswerState {
  return useContext(ExternalAnswerContext);
}

/**
 * The answer for `ref` on `source`, or null when the source was not resolved,
 * the question is absent, or the answer is blank.
 *
 * An unresolved source, a blank answer, and a care plan we could not load all
 * collapse to the same null, and all render as the node's `empty` placeholder.
 */
export function lookupExternalAnswer(
  answers: ExternalAnswerMap,
  source: string,
  ref: string
): string | null {
  const bySource = answers[source];
  if (bySource == null) return null;
  const raw = bySource[ref];
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}
